import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Navigate } from 'react-router';
import Button from '../../../../components/button/button.component';
import Loader from '../../../../components/loader/loader.component';
import TextHeader from '../../../../components/text-header/text-header.component';
import PageError from '../../../../components/page-error/page-error.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import VerificationValidator from './verification.validator';
import FormService from '../../../../services/form-service';
import useFormStructureModified from '../../../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import {
  verifyUser, resendVerificationCode, getUserVerificationData, resetActionHash,
} from './verification.action';
import { resetFormErrors } from '../../app.action';
import {
  ROUTE_DASHBOARD, ROUTE_LOGIN, ROUTE_PASSWORD_RECOVERY, ROUTE_VERIFICATION, ROUTE_NOT_FOUND, ROUTE_PARAM_HASH,
} from '../../../../constants/system/routes';
import {
  VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP, VERIFICATION_TYPE_PHONE_NUMBER_SMS,
} from '../../../../constants/entity/verification-types';
import { ACTION_TYPE_PASSWORD_RECOVERY } from '../../../../constants/entity/action-types';
import { FORM_ID_VERIFICATION } from '../../../../constants/system/form-ids';

const Verification: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const RESEND_TIMEOUT_SECONDS: number = 60;
  const RESEND_DISABLED_INTERVAL: number = 1000;

  const {
    isDataLoaded,
    currentUser,
    userVerificationData,
    actionHash,
    actionType,
    homepageRoute,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const dispatch: ReduxDispatch = useDispatch();

  const actionHashParam: string = routeParams[ROUTE_PARAM_HASH];

  const ACTION_TYPE_ROUTE_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [ACTION_TYPE_PASSWORD_RECOVERY]: ROUTE_PASSWORD_RECOVERY,
  }), []);

  const [isResendDisabled, setIsResendDisabled]: [boolean, Function] = useState(false);
  const [resendDisabledSeconds, setResendDisabledSeconds]: [number, Function] = useState(0);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [inlineErrorMessage, setInlineErrorMessage]: [string | null, Function] = useState(null);
  const [isFallbackOptionsContainerVisible, setIsFallbackOptionsContainerVisible]: [boolean, Function]
    = useState(false);

  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified);
  const actionTypeRoute: string = actionType && ACTION_TYPE_ROUTE_MAP[actionType];
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;
  const verificationTypeList: VerificationType[] = (userVerificationData
    && userVerificationData.verificationTypeList) || [];

  const fallbackOptionsClickDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isFallbackOptionsContainerVisible,
  ], [isFallbackOptionsContainerVisible]);

  const onResendCodeButtonClick: MouseEventHandlerFunction = useCallback((): void => {
    dispatch(resendVerificationCode({
      actionHash: actionHashParam,
    }, (): void => {
      setInlineErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setInlineErrorMessage(exception.message);
      }
    }));
    setIsResendDisabled(true);
  }, [actionHashParam]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(verifyUser({
        verificationForm: {
          code: values.code,
          actionHash: actionHashParam || null,
        },
      }, (response: ActionResponse): void => {
        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [actionHashParam]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [actionHashParam, formValues, withFormErrors]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (actionHashParam) {
      dispatch(resetActionHash());
      dispatch(getUserVerificationData({
        actionHash: actionHashParam,
      }, (): void => {
        setErrorMessage(null);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setErrorMessage(exception.message);
        }
      }));

      setIsResendDisabled(true);
    }
  }, [actionHashParam]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isResendDisabled) {
      setResendDisabledSeconds(RESEND_TIMEOUT_SECONDS);
    }

    let resendDisabledIndex: number = 0;
    const resendDisabledIntervalId: IntervalId | null = isResendDisabled ? setInterval((): void => {
      const currentResendDisabledSeconds: number = RESEND_TIMEOUT_SECONDS - resendDisabledIndex;

      resendDisabledIndex++;
      setResendDisabledSeconds(currentResendDisabledSeconds);

      if (resendDisabledIntervalId && currentResendDisabledSeconds === 0) {
        clearInterval(resendDisabledIntervalId);
      }
    }, RESEND_DISABLED_INTERVAL) : null;

    const resendDisabledTimeoutId: TimeoutId | null = isResendDisabled ? setTimeout((): void => {
      setIsResendDisabled(false);
    }, (RESEND_TIMEOUT_SECONDS + 1) * 1000) : null;

    return (): void => {
      if (resendDisabledIntervalId) {
        clearInterval(resendDisabledIntervalId);
      }
      if (resendDisabledTimeoutId) {
        clearTimeout(resendDisabledTimeoutId);
      }
    };
  }, [isResendDisabled]);

  const switchFallbackOptionsContainerVisibility: Function = useCallback((): void => {
    setIsFallbackOptionsContainerVisible(!isFallbackOptionsContainerVisible);
  }, [isFallbackOptionsContainerVisible]);

  const onFallbackOptionClick: Function = useCallback((verificationType: string): void => {
    dispatch(getUserVerificationData({
      actionHash: actionHashParam,
      type: verificationType,
    }, (): void => {
      setErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception && exception.message) {
        setErrorMessage(exception.message);
      }
    }));
  }, [actionHashParam]);

  useEffect((): void => {
    if (actionHash && actionHashParam && actionHash !== actionHashParam) {
      navigate({
        pathname: `${ROUTE_VERIFICATION}/${actionHash}`,
      });
    }
  }, [actionHash]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (currentUser && currentUser.id) {
    return <Navigate to={homepageRoute || ROUTE_DASHBOARD} />;
  }

  if (!actionHashParam) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (actionHash && actionTypeRoute) {
    return <Navigate to={`${actionTypeRoute}/${actionHash}`} />;
  }

  if (errorMessage) {
    return (
      <PageError
        isCentered
        message={errorMessage}
        extraComponent={(
          <TextBlock>
            Try to
            {' '}
            <FilteredLink link={ROUTE_LOGIN}>
              Sign In
            </FilteredLink>
            {' '}
            again.
          </TextBlock>
        )}
      />
    );
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <Form id={FORM_ID_VERIFICATION} onChange={onFormChange}>
      <FlexContainer isCentered>
        <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
          <Grid withLargeSpacing>
            <GridItem isFullWidth>
              <TextHeader>
                Verify it&apos;s you
              </TextHeader>
              {
                userVerificationData && userVerificationData.user && (
                  <TextBlock>
                    {
                      (userVerificationData.user.phone && (!userVerificationData.type
                        || userVerificationData.type === VERIFICATION_TYPE_PHONE_NUMBER_SMS)) && (
                        <Fragment>
                          We texted your phone +X XXXXXXXX
                          {userVerificationData.user.phone.slice(-2)}
                          .
                          {' '}
                          Please enter the code to sign in.
                        </Fragment>
                      )
                    }
                    {
                      userVerificationData.type === VERIFICATION_TYPE_AUTHENTICATOR_APP_OTP && (
                        <Fragment>
                          Enter the code from your Authenticator App to sign in.
                        </Fragment>
                      )
                    }
                  </TextBlock>
                )
              }
            </GridItem>
            <GridItem isFullWidth>
              <FormFieldNumber
                isShort
                isOutlined
                withNoCommas
                withNoAutocomplete
                name="code"
                label="Code"
              />
            </GridItem>
            <GridItem isFullWidth>
              {
                (userVerificationData && (!userVerificationData.type
                  || userVerificationData.type === VERIFICATION_TYPE_PHONE_NUMBER_SMS)) && (
                  <Button
                    isText
                    isDefault
                    isDisabled={isResendDisabled}
                    onClick={onResendCodeButtonClick}
                  >
                    Resend code
                    {
                      !!resendDisabledSeconds && resendDisabledSeconds > 0 && (
                        ` (${resendDisabledSeconds})`
                      )
                    }
                  </Button>
                )
              }
            </GridItem>
            <GridItem isFullWidth>
              <Button
                isActive
                isDefault
                isExtended
                isDisabled={isSaveDisabled}
                onClick={onSubmitForm}
              >
                Verify
              </Button>
            </GridItem>
            <GridItem isFullWidth>
              {
                verificationTypeList && !!verificationTypeList.length && (
                  <Fragment>
                    <FlexContainer isCentered isFullWidth>
                      <FilteredLink
                        isSwitcher
                        clickDeps={fallbackOptionsClickDeps}
                        onClick={switchFallbackOptionsContainerVisibility}
                      >
                        Fallback options
                      </FilteredLink>
                    </FlexContainer>
                    {
                      isFallbackOptionsContainerVisible && (
                        verificationTypeList.map((verificationType: VerificationType): ReactElement => (
                          <FlexContainer isCentered isFullWidth key={verificationType.id}>
                            <Button
                              isText
                              isActive
                              isExtended
                              onClick={(): void => {
                                onFallbackOptionClick(verificationType.name);
                              }}
                            >
                              {verificationType.title}
                            </Button>
                          </FlexContainer>
                        ))
                      )
                    }
                  </Fragment>
                )
              }
            </GridItem>
          </Grid>
          {
            (!!formError || !!inlineErrorMessage || !!mainServerFormError) && !isFormSaving && (
              <ErrorBlock errorMessage={formError || inlineErrorMessage || mainServerFormError}>
                <TextBlock>
                  Try to
                  {' '}
                  <FilteredLink link={ROUTE_LOGIN}>
                    Sign In
                  </FilteredLink>
                  {' '}
                  again.
                </TextBlock>
              </ErrorBlock>
            )
          }
        </PaddedFlexContainer>
      </FlexContainer>
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    verification,
  } = reducers;

  initialValues.code = null;

  return {
    isDataLoaded: !!verification.userVerificationData,
    currentUser: app.baseData && app.baseData.currentUser,
    userVerificationData: verification.userVerificationData,
    actionHash: verification.actionHash,
    actionType: verification.actionType,
    homepageRoute: verification.homepageRoute,
  };
};

const VerificationForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ verification }: Reducers): ValidationErrorsMap => (
    verification.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ verification }: Reducers): ValidationErrorsMap => (
    verification.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_VERIFICATION}
      component={Verification}
      validator={VerificationValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(VerificationForm);
